import { usePrevPageRouter } from '@graphcommerce/framer-next-pages'
import { IconSvg } from '@graphcommerce/next-ui'
import { useWatch } from '@graphcommerce/react-hook-form'
import { Trans } from '@lingui/react'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import useEventCallback from '@mui/material/utils/useEventCallback'

import { useRouter } from 'next/router'
import LocationIcon from '../Icons/LocationIcon.svg'
import PhoneIcon from '../Icons/PhoneIcon.svg'
import StoreIcon from '../Icons/StoreIcon.svg'
import { StoreInfoWindowFragment } from './StoreInfoWindow.gql'
import { useStoreLocatorForm } from './StoreLocatorFormProvider'

type InfoWindowProps = {
  content: StoreInfoWindowFragment
  listView?: boolean
}

export function StoreInfo(props: InfoWindowProps) {
  const { content, listView } = props
  const { phone, opening_times, opening_times_extra_text, path, curbside_enabled } = content
  const { setValue, control } = useStoreLocatorForm()
  const selectedStore = useWatch({ control, name: 'selectedStore' })
  const isSelectedStore = selectedStore?.path === path

  const prev = usePrevPageRouter()
  const { back } = useRouter()

  const setSelectedStore = useEventCallback(() => {
    if (!content) return
    setValue('selectedStore', content)
    if (prev?.pathname === '/checkout') {
      back()
    }
  })

  const getOpeningHours = () => {
    const d = new Date()
    const today = d.getDay()

    const storeData = opening_times?.filter((store) => store?.day_of_week === today)[0]
    const openToday = storeData && storeData?.status

    return openToday
      ? `Vandaag geopend · ${storeData.open} - ${storeData.close}`
      : 'Vandaag gesloten'
  }

  if (!content) return null

  return (
    <>
      <Box
        sx={(theme) => ({
          typography: 'body2',
          lineHeight: '20px',
          fontFamily: theme.typography.fontFamily,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          textAlign: 'center',
          color: 'primary.main',
          fontWeight: '600',
          gap: '5px',
          userSelect: 'none',
          mt: theme.spacings.xxs,

          ...(listView && {
            width: '100%',
            mb: theme.spacings.xs,
          }),

          '& > div': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
          },
          '& .IconSvg-root.sizeLarge': {
            fontSize: '55px',
          },
          '& .IconSvg-root.sizeLarge > use:focus': {
            outline: 'none',
          },
        })}
      >
        <Box>
          <Link
            href={`tel:${phone}`}
            color='inherit'
            underline='always'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              willChange: 'opacity',

              '&:hover': {
                opacity: '.8',
              },
            }}
          >
            <IconSvg
              sx={{
                color: 'primary.main',
              }}
              src={PhoneIcon}
              size='large'
            />
            {phone}
          </Link>
        </Box>

        <Box>
          {path && (
            <Link
              href={`/winkel/${path}`}
              color='inherit'
              underline='always'
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                willChange: 'opacity',

                '&:hover': {
                  opacity: '.8',
                },
              }}
            >
              <IconSvg src={StoreIcon} size='large' />
              Winkelpagina
            </Link>
          )}
        </Box>

        <Box>
          <Link
            color='inherit'
            underline='always'
            onClick={curbside_enabled && !isSelectedStore ? setSelectedStore : undefined}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: curbside_enabled ? 'inherit' : 'text.disabled',
              willChange: 'opacity',
              '&:hover': {
                opacity: curbside_enabled ? 0.8 : 1,
              },
            }}
          >
            <IconSvg
              src={LocationIcon}
              size='large'
              sx={{
                color: curbside_enabled ? 'inherit' : 'text.disabled',
              }}
            />
            {curbside_enabled && isSelectedStore ? (
              <Trans id='Selected' />
            ) : (
              <Trans id='Choose as your store' />
            )}
          </Link>
        </Box>
      </Box>

      <Box sx={{ typography: 'body2' }}>
        {getOpeningHours()}

        {opening_times_extra_text && (
          <div>
            <i>{opening_times_extra_text}</i>
          </div>
        )}
      </Box>
    </>
  )
}
